import React from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Image,
  Tag,
  Container,
  Spinner,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { MdLocalOffer } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import MarkdownRenderer from "../Common/MarkdownRenderer";
import SeeMoreButton from "../Common/SeeMoreButton";

const RecentBlogPosts = ({
  blogPosts,
  title = "Recent blog posts",
  showSeeMore = true,
  maxPosts = 3,
  onSeeMore,
  fromWhere = "blog",
  loadingProp = false,
  error = null,
}) => {
  const cardColor = fromWhere === "blog" ? "#000B8C" : "#244034";
  const navigate = useNavigate();

  // Handle loading state
  if (loadingProp) {
    return (
      <Container maxW="7xl" py={{ base: 8, md: 16 }}>
        <Flex justifyContent="center" alignItems="center" minH="200px">
          <Spinner size="xl" color={cardColor} />
        </Flex>
      </Container>
    );
  }

  // Handle error state
  if (error) {
    return (
      <Container maxW="7xl" py={{ base: 8, md: 16 }}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Text>{error}</Text>
        </Alert>
      </Container>
    );
  }

  // Handle empty or missing data
  if (!blogPosts || blogPosts.length === 0) {
    return (
      <Container maxW="7xl" py={{ base: 8, md: 16 }}>
        <Text textAlign="center" color="gray.500">
          No blog posts available.
        </Text>
      </Container>
    );
  }

  // Show all posts without any filtering
  const allPosts = blogPosts || [];

  // Take only the required number of posts
  const postsToShow = allPosts.slice(0, maxPosts);
  const hasMorePosts = allPosts.length > maxPosts;

  // If no posts available, show a message
  if (postsToShow.length === 0) {
    return (
      <Container maxW="7xl" py={{ base: 8, md: 16 }}>
        <Text textAlign="center" color="gray.500">
          No blog posts available.
        </Text>
      </Container>
    );
  }

  // Helper function to check if post has valid media
  const hasValidMedia = (post) => {
    const isPdf = post.image && 
      (post.image.toLowerCase().endsWith('.pdf') || 
       post.image.toLowerCase().includes('.pdf'));
    const hasValidImage =
      post.image &&
      !post.image.includes("/clients/") &&
      !post.image.includes("/png/placeholder.png") &&
      !post.image.includes("placeholder") &&
      !isPdf; // Exclude PDFs from image handling
    const hasValidVideo = post.isVideo && post.videoUrl;
    return hasValidImage || hasValidVideo || isPdf;
  };

  // Helper component for rendering markdown text
  const MarkdownText = ({ children, fontSize, color, noOfLines, ...props }) => {
    const hasMarkdown =
      children &&
      (children.includes("**") ||
        children.includes("*") ||
        children.includes("`") ||
        children.includes("#") ||
        children.includes("[") ||
        children.includes(">") ||
        children.includes("|") ||
        children.includes("---") ||
        children.includes("http") ||
        children.includes("<a"));

    if (!hasMarkdown) {
      return (
        <Text
          fontSize={fontSize}
          color={color}
          noOfLines={noOfLines}
          {...props}
        >
          {children}
        </Text>
      );
    }

    return (
      <Box
        fontSize={fontSize}
        color={color}
        noOfLines={noOfLines}
        sx={{
          "& p": {
            margin: "0",
            lineHeight: "inherit",
            color: color || "inherit",
          },
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            margin: "0",
            fontWeight: "bold",
            lineHeight: "inherit",
            fontSize: "inherit",
            color: color || "inherit",
          },
          "& *": {
            color: color || "inherit",
          },
        }}
        {...props}
      >
        <MarkdownRenderer content={children} />
      </Box>
    );
  };

  // Debug: Log the posts
  // Special debug for the 3rd post (index 2)
  if (postsToShow.length > 2) {
  }

  const handleCardClick = (blogPost, index) => {
    // Use blog ID or slug if available, otherwise fall back to index
    const blogId = blogPost.id || blogPost.slug || index + 1;

    // Prepare blog post data for navigation
    const postData = {
      ...blogPost,
      author:
        typeof blogPost.author === "object"
          ? blogPost.author?.fullName || blogPost.author?.name || "BA Systems"
          : blogPost.author || "BA Systems",
      hashtags: Array.isArray(blogPost.hashtags)
        ? blogPost.hashtags.map((tag) =>
            typeof tag === "object"
              ? tag.name || tag.text || String(tag)
              : String(tag)
          )
        : blogPost.hashtags
        ? [String(blogPost.hashtags)]
        : [],
    };

    if (fromWhere === "blog") {
      navigate(`/newsroom-details/${blogId}`, {
        state: { blogPost: postData, currentPage: 1 },
      });
    } else {
      navigate(`/team-activity/${blogId}`, {
        state: { blogPost: postData, currentPage: 1 },
      });
    }
  };

  return (
    <Container maxW="7xl" py={{ base: 8, md: 16 }} px={{ base: 4, md: 6 }}>
      <Heading
        as="h2"
        fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
        color="#222"
        mb={{ base: 6, md: 8 }}
        textAlign={fromWhere === "blog" ? "left" : "center"}
        fontWeight="semibold"
      >
        {title}
      </Heading>

      <Flex
        direction={{ base: "column", lg: "row" }}
        gap={{ base: 6, md: 8 }}
        align="stretch"
      >
        {/* Featured Card - Left (More Space) */}
        {postsToShow[0] && (
          <Box
            flex={{ base: "none", lg: 5 }} // Changed from 2 to 5 for more space
            position="relative"
            borderRadius="20px"
            maxH={{ base: "400px", md: "400px", lg: "500px" }}
            overflow="hidden"
            minH={{ base: "240px", md: "280px", lg: "320px" }}
            w="100%"
            cursor="pointer"
            onClick={() => handleCardClick(postsToShow[0], 0)}
            _hover={{
              transform: "translateY(-2px)",
              transition: "all 0.2s ease-in-out",
            }}
            bg={!hasValidMedia(postsToShow[0]) ? "white" : "white"}
            border="1px solid #e2e8f0"
            boxShadow="sm"
            p={4}
          >
            {hasValidMedia(postsToShow[0]) ? (
              <>
                {postsToShow[0].isVideo && postsToShow[0].videoUrl ? (
                  <Box position="relative" w="100%" h="100%" borderRadius="16px" overflow="hidden">
                    <video
                      src={postsToShow[0].videoUrl}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        minHeight: "240px",
                        borderRadius: "16px",
                      }}
                      autoPlay
                      muted
                      loop
                    />
                    <Box
                      position="absolute"
                      top={4}
                      right={4}
                      bg="rgba(0, 0, 0, 0.7)"
                      color="white"
                      px={2}
                      py={1}
                      borderRadius="md"
                      fontSize="sm"
                      fontWeight="bold"
                    >
                      VIDEO
                    </Box>
                  </Box>
                ) : postsToShow[0].image &&
                  !postsToShow[0].image.includes("placeholder") ? (
                  <Image
                    src={postsToShow[0].image}
                    alt={postsToShow[0]?.title || "Blog post"}
                    w="100%"
                    h="100%"
                    objectFit="contain"
                    borderRadius="16px"
                    minH={{ base: "240px", md: "280px", lg: "320px" }}
                    fallback={<Box w="100%" h="100%" bg="gray.200" borderRadius="16px" />}
                  />
                ) : null}

                {/* Conditional overlay for media posts */}
                {postsToShow[0]?.overlay && (
                  <Box
                    position="absolute"
                    left={0}
                    right={0}
                    bottom={0}
                    top={0}
                    bgGradient={
                      fromWhere === "blog"
                        ? "linear(to-t, #000B8C 0%, transparent 100%)"
                        : "linear(to-t, #215560 0%, transparent 100%)"
                    }
                  />
                )}

                <Box
                  position="absolute"
                  left={0}
                  right={0}
                  bottom={0}
                  p={{ base: 4, md: 6 }}
                  color="white"
                  zIndex={2}
                >
                  <Flex align="center" mb={2} gap={2}>
                    <Box as={MdLocalOffer} color="#E6FF3F" />
                    <Tag
                      color="#E6FF3F"
                      bg="rgba(255,255,255,0.08)"
                      fontWeight="medium"
                      fontSize={{ base: "sm", md: "md" }}
                      borderRadius="full"
                      px={3}
                      py={1}
                    >
                      {typeof postsToShow[0]?.tag === "object"
                        ? postsToShow[0].tag?.fullName ||
                          postsToShow[0].tag?.name ||
                          "News"
                        : postsToShow[0]?.tag || "News"}
                    </Tag>
                  </Flex>
                  <Text
                    fontSize={{ base: "lg", md: "xl", lg: "2xl" }}
                    fontWeight="bold"
                    noOfLines={2}
                    color="white"
                    lineHeight="1.2"
                  >
                    {postsToShow[0]?.title}
                  </Text>
                  {(postsToShow[0]?.post_desc || postsToShow[0]?.desc) && (
                    <MarkdownText
                      fontSize={{ base: "sm", md: "md" }}
                      mt={2}
                      noOfLines={3}
                      color="white" // Updated color for better contrast
                      opacity={0.9}
                    >
                      {postsToShow[0].post_desc || postsToShow[0].desc}
                    </MarkdownText>
                  )}
                </Box>
              </>
            ) : (
              // Text-only post without media
              <Box
                p={{ base: 4, md: 6 }}
                h="100%"
                display="flex"
                flexDirection="column"
                justifyContent="center"
              >
                <Flex align="center" mb={4} gap={2}>
                  <Box as={MdLocalOffer} color={cardColor} />
                  <Tag
                    color={cardColor}
                    bg="#F8F9F6"
                    fontWeight="medium"
                    fontSize={{ base: "sm", md: "md" }}
                    borderRadius="full"
                    px={3}
                    py={1}
                  >
                    {typeof postsToShow[0]?.tag === "object"
                      ? postsToShow[0].tag?.fullName ||
                        postsToShow[0].tag?.name ||
                        "News"
                      : postsToShow[0]?.tag || "News"}
                  </Tag>
                </Flex>
                <Text
                  fontSize={{ base: "xl", md: "2xl", lg: "3xl" }}
                  fontWeight="bold"
                  mb={4}
                  color="#222"
                  lineHeight="1.2"
                >
                  {postsToShow[0]?.title}
                </Text>
                {(postsToShow[0]?.post_desc || postsToShow[0]?.desc) && (
                  <MarkdownText
                    fontSize={{ base: "md", md: "lg" }}
                    color="gray.700"
                    noOfLines={4}
                    lineHeight="1.5"
                  >
                    {postsToShow[0].post_desc || postsToShow[0].desc}
                  </MarkdownText>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Secondary Card - Right (Single Post) */}
        {postsToShow[1] && (
          <Box flex={{ base: "none", lg: 2 }} w="100%">
            <Box
              position="relative"
              borderRadius="20px"
              overflow="hidden"
              minH={{ base: "240px", md: "280px", lg: "320px" }}
              maxH={{ base: "400px", md: "400px", lg: "500px" }}
              w="100%"
              cursor="pointer"
              onClick={() => handleCardClick(postsToShow[1], 1)}
              _hover={{
                transform: "translateY(-2px)",
                transition: "all 0.2s ease-in-out",
              }}
              bg="white"
              border="1px solid #e2e8f0"
              boxShadow="sm"
              p={4}
            >
              {hasValidMedia(postsToShow[1]) ? (
                <Flex direction="column" h="100%" borderRadius="16px" overflow="hidden">
                  {/* Image Section - Top */}
                  <Box flex="1" position="relative" minH="60%">
                    {postsToShow[1].isVideo && postsToShow[1].videoUrl ? (
                      <Box position="relative" w="100%" h="100%">
                        <video
                          src={postsToShow[1].videoUrl}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderTopLeftRadius: "20px",
                            borderTopRightRadius: "20px",
                          }}
                          autoPlay
                          muted
                          loop
                        />
                        <Box
                          position="absolute"
                          top={4}
                          right={4}
                          bg="rgba(0, 0, 0, 0.7)"
                          color="white"
                          px={2}
                          py={1}
                          borderRadius="md"
                          fontSize="sm"
                          fontWeight="bold"
                        >
                          VIDEO
                        </Box>
                      </Box>
                    ) : (
                      <Image
                        src={postsToShow[1].image}
                        alt={postsToShow[1]?.title || "Blog post"}
                        w="100%"
                        h="100%"
                        objectFit="cover"
                        borderTopLeftRadius="20px"
                        borderTopRightRadius="20px"
                        fallback={
                          <Box
                            w="100%"
                            h="100%"
                            bg="gray.200"
                            borderTopLeftRadius="20px"
                            borderTopRightRadius="20px"
                          />
                        }
                      />
                    )}
                  </Box>

                  {/* Text Section - Bottom */}
                  <Box
                    bg="white"
                    p={{ base: 3, md: 4 }}
                    borderBottomLeftRadius="12px"
                    borderBottomRightRadius="12px"
                  >
                    <Flex align="center" mb={2} gap={2}>
                      <Box as={MdLocalOffer} color={cardColor} />
                      <Tag
                        color={cardColor}
                        bg="#F8F9F6"
                        fontWeight="medium"
                        fontSize={{ base: "xs", md: "sm" }}
                        borderRadius="full"
                        px={2}
                        py={1}
                      >
                        {typeof postsToShow[1]?.tag === "object"
                          ? postsToShow[1].tag?.fullName ||
                            postsToShow[1].tag?.name ||
                            "News"
                          : postsToShow[1]?.tag || "News"}
                      </Tag>
                    </Flex>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      fontWeight="bold"
                      noOfLines={2}
                      color="#222"
                      lineHeight="1.2"
                      mb={2}
                    >
                      {postsToShow[1]?.title || ""}
                    </Text>
                    {(postsToShow[1]?.post_desc || postsToShow[1]?.desc) && (
                      <MarkdownText
                        fontSize={{ base: "sm", md: "sm" }}
                        noOfLines={12}
                        color="gray.600"
                        lineHeight="1.4"
                      >
                        {postsToShow[1].post_desc || postsToShow[1].desc}
                      </MarkdownText>
                    )}
                  </Box>
                </Flex>
              ) : (
                // Text-only post without media for right side
                <Box
                  p={{ base: 3, md: 4 }}
                  h="100%"
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                >
                  <Flex align="center" mb={3} gap={2}>
                    <Box as={MdLocalOffer} color={cardColor} />
                    <Tag
                      color={cardColor}
                      bg="#F8F9F6"
                      fontWeight="medium"
                      fontSize={{ base: "xs", md: "sm" }}
                      borderRadius="full"
                      px={2}
                      py={1}
                    >
                      {typeof postsToShow[1]?.tag === "object"
                        ? postsToShow[1].tag?.fullName ||
                          postsToShow[1].tag?.name ||
                          "News"
                        : postsToShow[1]?.tag || "News"}
                    </Tag>
                  </Flex>
                  <Text
                    fontSize={{ base: "md", md: "lg", lg: "xl" }}
                    fontWeight="bold"
                    mb={2}
                    color="#222"
                    lineHeight="1.2"
                  >
                    {postsToShow[1]?.title}
                  </Text>
                  {(postsToShow[1]?.post_desc || postsToShow[1]?.desc) && (
                    <MarkdownText
                      fontSize={{ base: "sm", md: "md" }}
                      color="gray.600"
                      noOfLines={3}
                      lineHeight="1.5"
                    >
                      {postsToShow[1].post_desc || postsToShow[1].desc}
                    </MarkdownText>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Flex>

      {/* See More Button */}
      {showSeeMore && hasMorePosts && fromWhere !== "team_activity" && (
        <SeeMoreButton
          onClick={() => navigate("/newsroom")}
          text="See More"
          colors={{
            textColor: "black",
            textBorder: "1px solid black",
            textHover: "#000B8C",
            iconBg: "rgba(0, 11, 140, 1)",
            iconHover: "#030b3aff",
          }}
        />
      )}
    </Container>
  );
};

export default RecentBlogPosts;
